import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  // DrawingManager, // ✅ REMOVED — DrawingManager is no longer available in Maps JS API v3.65+
  GoogleMap,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';

import { useMutation, useQueryClient } from 'react-query';
import * as GeofenceService from '../../../../services/bioplus-services/geofence';
import { deleteApiData, updateApiData } from '../../../../Components/Api';
import { useUserContext } from '../../../../hooks/UserContext';
import { calculateArea } from '../../../../helper/calculateAreaHelper';
// import UserDefaultImage from '../../../../assets/PNG/default_user.png';
import {
  AddGeofenceInfoBanner,
  CloseIcon,
  // InfoIconWrapper,
  InfoText,
  MapButton,
  MapCard,
  MapCardActions,
  MapCardActionsWrapper,
  MapCardContent,
  MapHeaderContainer,
  MapHeaderTitle,
  MapLabel,
  MapSearchInput,
  OutlineButton,
  // StyledInfoIcon,
  TextButton,
  // Tooltip,
} from '../geofence.style';
import {
  AddPlusIcon,
  CloseIconWhite,
  DeleteIcon,
  EditIcon,
  // UserDefaultProfile,
} from '../../../../Components/icons';
import {
  IconButton,
  SwitchButtonWrapper,
  SwitcherLabel,
  TwoColumns,
} from '../../../../services/settings/appStyle/global.style';
import { Button, InputField, Loader, Switch } from '../../../../Components/uielements';
import DropDown from '../../../../Components/uielements/dropDown';
import { formatDropdownData } from '../helper';
import { IntlMessages } from '../../../../helper/langHelper';
import { useIntl } from 'react-intl';
import { convertTo6DigitHex, convertTo8DigitHex } from '../../../../helper/colorHelper';
import { ModalLabel } from '../../UserV2/user.style';
import {
  getBoundsFromRadius,
  isValidPremiseRange,
  //  getColorFromId, getInitials
} from './helper';
// import MasterGeofencePopUpInfo from '../modals/masterGeofenceInfoModal';
// import { MapsPinDrop } from 'material-ui/svg-icons';
import { getCheckListForDropDown } from '../../../../services/bioplus-services/checkList';
import { getOverlay } from '../GeofenceMap';
import FriendLocationContainer from './Friendlocationcontainer';
import { useCompany } from '../../../../hooks/CompanyContext';

// ✅ 'drawing' removed — DrawingManager is no longer available in Maps JS API v3.65+
export const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

const createGeofence = (userRole, newGeofence) => {
  switch (userRole.role) {
    case 'PARTNER':
      // return GeofenceService.partnerCreateGeofence(userRole.companyId, newGeofence);
      return GeofenceService.createGeofence(newGeofence);
    default:
      return GeofenceService.createGeofence(newGeofence);
  }
};

// ---------------------------------------------------------------------------
// ✅ Replaces DrawingManager polygon mode.
// User clicks to place points, double-clicks to finish.
// Calls onPolygonComplete(googlePolygon) identically to DrawingManager.
// ---------------------------------------------------------------------------
function useCustomPolygonDrawing({ isActive, onPolygonComplete }) {
  const [previewPath, setPreviewPath] = useState([]);
  const [cursorPoint, setCursorPoint] = useState(null);

  useEffect(() => {
    if (!isActive) {
      setPreviewPath([]);
      setCursorPoint(null);
    }
  }, [isActive]);

  const handleMapClick = useCallback(
    (e) => {
      if (!isActive) return;
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setPreviewPath((prev) => [...prev, point]);
    },
    [isActive]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isActive) return;
      setCursorPoint({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [isActive]
  );

  const handleDblClick = useCallback(
    (e) => {
      if (!isActive) return;
      e.stop?.();
      setPreviewPath((prev) => {
        const finalPath = [...prev];
        if (finalPath.length < 3) return prev;

        // Build a google.maps.Polygon — same object shape DrawingManager passed to onPolygonComplete
        const googlePolygon = new window.google.maps.Polygon({
          paths: finalPath,
          map: null,
        });

        onPolygonComplete(googlePolygon);
        setCursorPoint(null);
        return [];
      });
    },
    [isActive, onPolygonComplete]
  );

  const livePreviewPath =
    isActive && cursorPoint && previewPath.length > 0 ? [...previewPath, cursorPoint] : previewPath;

  return { handleMapClick, handleMouseMove, handleDblClick, livePreviewPath };
}
// ---------------------------------------------------------------------------

const MapComponent = ({
  markers,
  geofenceData,
  premises,
  fetchData,
  stateData,
  geoFence,
  isView,
  navigate,
  isViewFriendLocation,
  canManageGeofence,
  canCreateGeofence,
}) => {
  const mapRef = useRef();
  const polygonRefs = useRef([]);
  const activePolygonIndex = useRef();
  const autocompleteRef = useRef();
  const drawingManagerRef = useRef();
  const distanceMarkersRef = useRef([]);
  const distanceLinesRef = useRef([]);
  const areaInfoWindowRef = useRef(null);
  const currentPolygonListeners = useRef([]);
  const didInitialFitRef = useRef(false);

  const [activeId, setActiveId] = useState('');
  const [addGeofenceModal, setAddGeofenceModal] = useState(false);
  const [activeCoordinates, setActiveCoordinates] = useState();
  const [tempCoordinates, setTempCoordinates] = useState();
  const [polygons, setPolygons] = useState(markers);
  const [drawingMode, setDrawingMode] = useState('');
  const [activePolygon, setActivePolygon] = useState();
  const [viewerLocation, setViewerLocation] = useState();
  const [premise, setPremise] = useState();
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklistOptions, setChecklistOptions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [divertAlert, setDivertAlert] = useState(false);
  const [isDivertAlertChecked, setIsDivertAlertChecked] = useState(false);
  const [masterGeofence, setMasterGeofence] = useState(undefined);

  const [isPremiseSelected, setIsPremiseSelected] = useState(false);
  const [showAddGeofenceInfo, setShowAddGeofenceInfo] = useState(false);
  const [isPremiseLocked, setIsPremiseLocked] = useState(false);

  // Tracks whether the user clicked "Add Geofence" and actually drew a shape (not yet saved via API)
  const [isAddingNewGeofence, setIsAddingNewGeofence] = useState(false);
  const [hasLocalDrawnPolygon, setHasLocalDrawnPolygon] = useState(false);

  const defaultPositon = stateData ? stateData[0] : { lat: 40.7706771, lng: -99.7807839 };

  const defaultCenter = defaultPositon;

  const [center, setCenter] = useState(defaultCenter);

  const [geofenceDetail, setGeofenceDetail] = useState({
    index: '',
    id: '',
    name: '',
    area: '',
    premiseId: '',
    premiseName: '',
    createdby: '',
    declarationForm: '',
    speedLimit: '',
    declarationType: '',
    questionnaireId: '',
    divertAlert: false,
  });
  const [color, setColor] = useState(geofenceDetail?.color || '#E4836F');

  useEffect(() => {
    if (geofenceDetail?.color) {
      setColor(geofenceDetail.color);
    }
  }, [geofenceDetail.color]);

  const [geofenceTempData, setGeofenceTempData] = useState({
    index: '',
    id: '',
    name: '',
    area: '',
    premiseId: '',
    premiseName: '',
    createdby: '',
    declarationForm: '',
    speedLimit: '',
    declarationType: '',
    questionnaireId: '',
    divertAlert: false,
  });

  const userRole = useUserContext()?.company;
  const userInfo = useUserContext()?.user;
  const isAdmin = userRole.role === 'ADMIN';
  const isVisitor = userRole?.role === 'VISITOR';
  const companyFilter = useCompany()?.company?._id;
  const isAdminOrIsPartner = userRole.role === 'ADMIN' || userRole.role === 'PARTNER';

  // format language
  const intl = useIntl();
  const alertMessageGivenPremission = intl.formatMessage({
    id: 'help.text.give.permission.geolocation',
  });
  const alertMessageSuccessfullyDeletedGeofence = intl.formatMessage({
    id: 'delete.geofence.success.info.text',
  });
  const alertMessageSuccessfullyUpdatedGeofence = intl.formatMessage({
    id: 'update.geofence.success.info.text',
  });

  const queryClient = useQueryClient();
  const mutation = useMutation(
    (newGeofence) => {
      return createGeofence(userRole, newGeofence);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('geofences');
        geofenceData.push(data);
      },
    }
  );

  // Fetch checklist options on component mount
  useEffect(() => {
    const fetchChecklists = async () => {
      try {
        const response = await getCheckListForDropDown({
          companyId: companyFilter ?? userRole?.companyId?._id,
        });
        if (response?.checklists) {
          setChecklistOptions(response.checklists);
        }
      } catch (error) {
        console.error('Error fetching checklists:', error);
      }
    };

    if (userRole?.companyId && !isVisitor) {
      fetchChecklists();
    }
  }, [userRole?.companyId, isVisitor]);

  useEffect(() => {
    if (isView) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!!position.coords) {
            setViewerLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          }
        },
        (error) => {
          console.error('Error accessing location:', error);
          alert(alertMessageGivenPremission);
        }
      );
    }
  }, []);

  useEffect(() => {
    setCenter(viewerLocation ?? defaultCenter);
  }, [viewerLocation]);

  useEffect(() => {
    setPolygons(markers);
  }, [markers]);

  useEffect(() => {
    if (!mapRef.current || isView === true || didInitialFitRef.current || polygons.length === 0) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    getOverlay(geoFence)?.forEach((polygon) => {
      bounds.extend(polygon);
    });

    mapRef.current.fitBounds(bounds, {
      top: 50,
      bottom: 50,
      left: 50,
      right: 50,
    });

    didInitialFitRef.current = true;
  }, [polygons, isView]);

  // Enhanced measurement clearing function
  const clearAllMeasurements = () => {
    // Clear distance markers
    if (distanceMarkersRef.current && distanceMarkersRef.current.length > 0) {
      distanceMarkersRef.current.forEach((marker) => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      distanceMarkersRef.current = [];
    }

    // Clear distance lines
    if (distanceLinesRef.current && distanceLinesRef.current.length > 0) {
      distanceLinesRef.current.forEach((line) => {
        if (line && line.setMap) {
          line.setMap(null);
        }
      });
      distanceLinesRef.current = [];
    }

    // Clear area info window
    if (areaInfoWindowRef.current) {
      if (areaInfoWindowRef.current.close) {
        areaInfoWindowRef.current.close();
      }
      areaInfoWindowRef.current = null;
    }

    // Clear polygon listeners
    if (currentPolygonListeners.current && currentPolygonListeners.current.length > 0) {
      currentPolygonListeners.current.forEach((listener) => {
        if (listener && window.google?.maps?.event?.removeListener) {
          window.google.maps.event.removeListener(listener);
        }
      });
      currentPolygonListeners.current = [];
    }
  };

  const polygonZIndexMap = useMemo(() => {
    if (!polygons || polygons.length === 0 || !window.google?.maps?.geometry) return {};

    const areas = polygons.map((path, idx) => {
      try {
        const latLngPath = path.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
        return { idx, area: window.google.maps.geometry.spherical.computeArea(latLngPath) };
      } catch {
        return { idx, area: 0 };
      }
    });

    // Biggest area first => lowest zIndex. Smallest area => highest zIndex (on top).
    const sorted = [...areas].sort((a, b) => b.area - a.area);
    const map = {};
    sorted.forEach((item, rank) => {
      map[item.idx] = rank + 1;
    });
    return map;
  }, [polygons]);

  // Robust polygon measurement function
  const updatePolygonMeasurements = (polygon) => {
    if (!polygon || !window.google?.maps?.geometry) {
      console.warn('Polygon or Google Maps geometry library not available');
      return;
    }

    try {
      const path = polygon.getPath();
      if (!path || path.getLength() < 3) {
        console.warn('Invalid polygon path');
        return;
      }

      // Clear previous measurements
      clearAllMeasurements();

      const coordinates = [];
      const edgeDistances = [];

      // Calculate distances and create markers for each edge
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        coordinates.push({ lat: point.lat(), lng: point.lng() });

        const nextIndex = (i + 1) % path.getLength();
        const nextPoint = path.getAt(nextIndex);

        // Calculate distance between current and next point
        const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
          point,
          nextPoint
        );
        edgeDistances.push(distance);

        // Calculate midpoint for label
        const midPoint = {
          lat: (point.lat() + nextPoint.lat()) / 2,
          lng: (point.lng() + nextPoint.lng()) / 2,
        };

        // Create distance label with better formatting
        const distanceText =
          distance >= 1000 ? `${(distance / 1000).toFixed(2)} km` : `${distance.toFixed(0)} m`;

        // Create marker for distance label
        const marker = new window.google.maps.Marker({
          position: midPoint,
          label: {
            text: distanceText,
            color: '#000',
            fontSize: '14px',
            fontWeight: '600',
            className: 'distance-label',
            zIndex: 1001,
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 0,
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: 'transparent',
            strokeWeight: 0,
          },
          map: mapRef.current,
        });

        distanceMarkersRef.current.push(marker);

        // Create line between points for better visibility
        const line = new window.google.maps.Polyline({
          path: [
            { lat: point.lat(), lng: point.lng() },
            { lat: nextPoint.lat(), lng: nextPoint.lng() },
          ],
          strokeColor: '#0071E3',
          strokeOpacity: 0.6,
          strokeWeight: 2,
          map: mapRef.current,
          zIndex: 999,
        });

        distanceLinesRef.current.push(line);
      }

      // Calculate total area
      const area = window.google.maps.geometry.spherical.computeArea(path);

      // Get polygon center for area label
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      const polygonCenter = bounds.getCenter();

      // Create area info window with enhanced styling
      const areaKm2 = (area / 1000000).toFixed(3);
      const areaM2 = area.toFixed(2);

      areaInfoWindowRef.current = new window.google.maps.InfoWindow({
        content: `
          <div style="
            font-family: Arial, sans-serif;
            padding: 8px;
            min-width: 150px;
            background: white;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          ">
            <div style="font-size: 14px; font-weight: 700; color: #0071E3; margin-bottom: 8px;">
              Polygon Measurements
            </div>
            <div style="font-size: 12px; color: #333; line-height: 1.6;">
              <div style="margin-bottom: 4px;">
                <strong>Area:</strong> ${areaKm2} km² (${areaM2} m²)
              </div>
            </div>
          </div>
        `,
        position: polygonCenter,
        disableAutoPan: true,
      });

      // if (isView && activeId === '' && !addGeofenceModal) {
      //   areaInfoWindowRef.current.open(mapRef.current);
      // }

      // Update state with calculated data
      setGeofenceTempData((prev) => ({
        ...prev,
        area: parseFloat(areaM2),
        edgeDistances,
        polygon: coordinates,
      }));
    } catch (error) {
      console.error('Error updating polygon measurements:', error);
    }
  };

  // Setup polygon listeners for real-time updates
  const setupPolygonListeners = (polygon) => {
    if (!polygon || !window.google?.maps?.event) return;

    // Clear existing listeners
    if (currentPolygonListeners.current && currentPolygonListeners.current.length > 0) {
      currentPolygonListeners.current.forEach((listener) => {
        window.google.maps.event.removeListener(listener);
      });
      currentPolygonListeners.current = [];
    }

    const path = polygon.getPath();

    // Add listeners for path changes
    const listeners = [
      window.google.maps.event.addListener(path, 'insert_at', () => {
        updatePolygonMeasurements(polygon);
      }),
      window.google.maps.event.addListener(path, 'set_at', () => {
        updatePolygonMeasurements(polygon);
      }),
      window.google.maps.event.addListener(path, 'remove_at', () => {
        updatePolygonMeasurements(polygon);
      }),
    ];

    currentPolygonListeners.current = listeners;
  };

  var loadedPolygons = [];

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyB1KCluOXIh5ipV4wcHe3z-ottrOfel1As',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const containerStyle = {
    boxSizing: 'border-box',
    borderRadius: '5px',
    width: '100%',
    height: '77vh',
    border: '0.6px solid #0071E3',
    boxShadow: '0px 0px 30px #0071e329',
  };

  const handleSelectedPremise = (value) => {
    setPremise(value);
  };

  const resetGeofenceCreation = () => {
    setAddGeofenceModal(false);
    setDrawingMode('');
    setIsPremiseSelected(false);
    setIsPremiseLocked(false);
    setPremise();
    setActivePolygon();
    setSelectedChecklist(null);
    setDivertAlert(false);
    setColor('#E4836F');
    setMasterGeofence(undefined);
    clearAllMeasurements();
    // If user drew a local polygon (not yet saved via API), remove it from the map
    if (hasLocalDrawnPolygon) {
      setPolygons((prev) => prev.slice(0, -1));
    }
    setIsAddingNewGeofence(false);
    setHasLocalDrawnPolygon(false);
    onDeleteDrawing();
  };

  const handleSelectedPremiseCreate = (value) => {
    if (!value) return;

    setPremise(value);
    setIsPremiseSelected(true);
    setIsPremiseLocked(true);

    // Clear any existing drawing and measurements
    clearAllMeasurements();
    onDeleteDrawing();
    setActivePolygon(undefined);

    const selectedPremise = premises?.find((premise) => premise._id === value.value);

    // if (selectedPremise?.masterGeofenceId) {
    //   setMasterGeofence(selectedPremise);
    // } else {
    //   setMasterGeofence(undefined);
    // }
    // as per old logic we assign one geofence as master geofence then based on that geofence points we set master centroid now stewart remove that master geofence to premise again which was there in 1st place so not working for new premise map restriction
    if (selectedPremise?.masterCentroid) {
      setMasterGeofence(selectedPremise);

      const lat = Number(selectedPremise.masterCentroid.latitude);
      const lng = Number(selectedPremise.masterCentroid.longitude);

      if (mapRef.current && Number.isFinite(lat) && Number.isFinite(lng)) {
        mapRef.current.panTo({ lat, lng });
        mapRef.current.setZoom(10);
      }
    } else {
      setMasterGeofence(undefined);
    }

    // Enable drawing mode
    setDrawingMode('');
    setTimeout(() => setDrawingMode('polygon'), 0);
  };

  const handleSelectedChecklist = (value) => {
    setSelectedChecklist(value);
  };

  const polygonOptions = (index) => {
    if (index >= 0 && geofenceData) {
      if (geofenceData[index] === undefined) {
        return {
          fillOpacity: 0.3,
          fillColor: geofenceData[index]?.color
            ? `${convertTo6DigitHex(geofenceData[index]?.color)}`
            : '#E4836F',
          strokeColor: geofenceData[index]?.color
            ? `${convertTo6DigitHex(geofenceData[index]?.color)}`
            : '#E4836F',
          strokeWeight: 2,
        };
      } else {
        return {
          fillColor: geofenceData[index]?.color
            ? `${convertTo6DigitHex(geofenceData[index]?.color)}`
            : '#E4836F',
          fillOpacity: 0.35,
          strokeColor: geofenceData[index]?.color
            ? `${convertTo6DigitHex(geofenceData[index]?.color)}`
            : '#E4836F',
          strokeWeight: 2,
        };
      }
    } else {
      return {
        fillColor: color,
        fillOpacity: 0.35,
        strokeColor: color,
        strokeWeight: 2,
      };
    }
  };

  // ✅ drawingManagerOptions kept as-is (unused now but preserved)
  const drawingManagerOptions = {
    polygonOptions: polygonOptions,
    drawingControl: false,
    drawingControlOptions: {
      position: window.google?.maps?.ControlPosition?.TOP_LEFT,
      drawingModes: [window.google?.maps?.drawing?.OverlayType?.POLYGON],
    },
  };

  const onLoadMap = (map) => {
    mapRef.current = map;
  };

  const onLoadPolygon = (polygon, index) => {
    loadedPolygons.push(index);
    polygonRefs.current[index] = polygon;
  };

  const onClickPolygon = (index) => {
    if (activeId !== index) {
      polygons[activeId] = tempCoordinates;
      setActiveId('');
      clearAllMeasurements();
    }

    const geofenceDeatilsObject = {
      index: index,
      id: geofenceData[index]._id,
      name: geofenceData[index].name,
      // m2
      // area: Math.round(calculateArea(geofenceData[index]?.polygon) * 100) / 100,
      // calculateArea() returns area in m² — convert to hectares divided mi2/1000 , rounded to 2 decimals.
      area: Math.round((calculateArea(geofenceData[index]?.polygon) / 10000) * 100) / 100,
      premiseId: geofenceData[index].premiseId,
      premiseName: geofenceData[index]?.premiseId?.propertyName,
      createdby:
        geofenceData[index]?.createdBy?.firstName + ' ' + geofenceData[index]?.createdBy?.lastName,
      declarationForm: geofenceData[index]?.declarationType ? 'Yes' : 'No',
      purpose: geofenceData[index].purpose,
      speedLimit: geofenceData[index]?.speedLimit,
      declarationType: geofenceData[index]?.declarationType,
      questionnaireId: geofenceData[index]?.questionnaireId,
      accountOwner: geofenceData[index]?.companyId?.name,
      createdBy: geofenceData[index]?.createdBy,
      divertAlert: geofenceData[index]?.divertAlert,
      color: geofenceData[index]?.color
        ? convertTo6DigitHex(geofenceData[index]?.color)
        : '#E4836F',
    };
    setIsDivertAlertChecked(geofenceDeatilsObject?.divertAlert ?? false);
    setGeofenceDetail(geofenceDeatilsObject);
    setGeofenceTempData(geofenceDeatilsObject);

    // Set selected checklist based on existing data
    const existingChecklist = checklistOptions.find(
      (option) => option.questionnaireId === geofenceData[index]?.questionnaireId
    );
    if (existingChecklist) {
      setSelectedChecklist({
        value: existingChecklist.value,
        label: existingChecklist.label,
        questionnaireId: existingChecklist.questionnaireId,
      });
    }

    activePolygonIndex.current = index;
  };

  const onLoadAutocomplete = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    const { geometry } = autocompleteRef.current.getPlace();
    const bounds = new window.google.maps.LatLngBounds();
    if (geometry.viewport) {
      bounds.union(geometry.viewport);
    } else {
      bounds.extend(geometry.location);
    }
    mapRef.current.fitBounds(bounds);
  };

  // ✅ onLoadDrawingManager kept as-is (ref still exists, just unused)
  const onLoadDrawingManager = (drawingManager) => {
    drawingManagerRef.current = drawingManager;
  };

  // ✅ onOverlayComplete kept exactly as-is
  const onOverlayComplete = ($overlayEvent) => {
    drawingManagerRef.current.setDrawingMode(null);
    if ($overlayEvent.type === window.google.maps.drawing.OverlayType.POLYGON) {
      const newPolygon = $overlayEvent?.overlay
        ?.getPath()
        ?.getArray()
        ?.map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

      const startPoint = newPolygon[0];
      newPolygon.push(startPoint);
      $overlayEvent.overlay?.setMap(null);
      setPolygons([...polygons, newPolygon]);
    }
  };

  // ✅ onPolygonComplete kept exactly as-is — called by useCustomPolygonDrawing
  const onPolygonComplete = useCallback(
    (polygon) => {
      if (!isPremiseSelected) {
        polygon.setMap(null);
        alert('Please select a property before drawing a geofence');
        return;
      }

      // Initial measurement update
      updatePolygonMeasurements(polygon);

      // Setup listeners for real-time updates
      setupPolygonListeners(polygon);

      const path = polygon.getPath();
      const coordinates = path.getArray().map((latLng) => ({
        lat: latLng.lat(),
        lng: latLng.lng(),
      }));

      setActivePolygon(coordinates);
      setActiveId(polygons.length);
      setPolygons([...polygons, coordinates]);
      setAddGeofenceModal(true);
      setHasLocalDrawnPolygon(true); // mark that a local (unsaved) polygon exists on the map
      setDrawingMode(''); // exit drawing mode after polygon is complete
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPremiseSelected, polygons.length]
  );

  const onDeleteDrawing = (index) => {
    clearAllMeasurements();
    // if (index) {
    //   setPolygons(polygons.slice(index, 1));
    // } else {
    //   setPolygons(polygons.slice(0, -1));
    // }
  };

  const onEditPolygon = (index) => {
    const polygonRef = polygonRefs.current[index];
    if (!polygonRef) return;

    // Update measurements
    updatePolygonMeasurements(polygonRef);

    // Setup listeners if not already set
    setupPolygonListeners(polygonRef);

    const coordinates = polygonRef
      .getPath()
      .getArray()
      .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

    const allPolygons = [...polygons];
    allPolygons[index] = coordinates;

    setActivePolygon(coordinates);
    setActiveCoordinates(coordinates);
    setPolygons(allPolygons);
  };

  function editGeofence(index) {
    setActiveId(geofenceDetail.index);
    setTempCoordinates(polygons[geofenceDetail.index]);

    // Find the premise for this geofence and set master bounds
    const currentGeofence = geofenceData[geofenceDetail.index];
    if (currentGeofence?.premiseId) {
      const premiseId =
        typeof currentGeofence.premiseId === 'object'
          ? currentGeofence.premiseId._id
          : currentGeofence.premiseId;

      const selectedPremise = premises?.find((p) => p._id === premiseId);

      if (selectedPremise?.masterCentroid) {
        setMasterGeofence(selectedPremise);
      } else {
        setMasterGeofence(undefined);
      }
    }
  }

  function toggleDrawingMode() {
    if (drawingMode !== 'polygon') {
      setDrawingMode('polygon');
      setGeofenceDetail({
        index: '',
        id: '',
        name: '',
        area: '',
        premiseId: '',
        premiseName: '',
        createdby: '',
        declarationForm: '',
        questionnaireId: '',
      });
    } else {
      setDrawingMode('');
      clearAllMeasurements();
    }
  }

  async function addGeofence(id, event) {
    try {
      event.preventDefault();
      if (event.target['name'].value.length < 1) {
        alert('Field Name cannot be Empty');
        return;
      }

      setIsProcessing(true);
      let geoPoints = [];
      activePolygon.map((data) => geoPoints.push({ latitude: data.lat, longitude: data.lng }));

      const payload = {
        name: event.target['name'].value,
        speedLimit: isVisitor ? 0 : event.target['speedLimit'].value,
        premiseId: isVisitor ? premises?._id : premise?.value,
        contactNumber: userInfo?.phone,
        contactEmail: userInfo?.email,
        contactName: userInfo?.firstName?.trim() + ' ' + userInfo?.lastName?.trim(),
        polygon: geoPoints,
        premiseName: isVisitor ? premises?.propertyName : premise?.label,
        color: convertTo8DigitHex(color),
        declarationType: isVisitor ? 'NONE' : selectedChecklist?.value || 'DEFAULT',
        roleId: userRole?._id,
        divertAlert: divertAlert,
        // geofenceTempData.area is in m² (set by updatePolygonMeasurements) — convert to hectares, 2 decimals
        area: Math.round((geofenceTempData.area / 10000) * 100) / 100,
        // edgeDistances: geofenceTempData.edgeDistances,
      };

      // Add questionnaireId if checklist is CUSTOM or DEFAULT
      if (
        (selectedChecklist?.value === 'CUSTOM' || selectedChecklist?.value === 'DEFAULT') &&
        selectedChecklist?.questionnaireId
      ) {
        payload.questionnaireId = selectedChecklist.questionnaireId;
      }

      await mutation.mutateAsync(payload);
      setHasLocalDrawnPolygon(false); // polygon is now saved via API, don't remove it on reset
      resetGeofenceCreation();

      setIsProcessing(false);
      setAddGeofenceModal(false);
      setDrawingMode('');
      setSelectedChecklist(null);
      clearAllMeasurements();
      setActiveId('');

      // Invalidate allGeofences query
      queryClient.invalidateQueries('allGeofences');

      fetchData();
    } catch (error) {
      setIsProcessing(false);
      if (activePolygon === undefined) {
        alert('Please draw a geofence to create it.');
      } else {
        alert(error?.response?.data?.message);
      }
    }
    // finally {
    //   setActiveId('');
    // }
  }

  function deleteGeofence(index) {
    let geofenceIndex = geofenceData.findIndex((value) => value.id === index);
    if (window.confirm('Are you sure that you want to delete the geofence ?')) {
      deleteApiData('geofences/', index, localStorage.getItem('session'))
        .then((data) => {
          alert(alertMessageSuccessfullyDeletedGeofence);
          setGeofenceDetail({
            index: '',
            id: '',
            name: '',
            area: '',
            premiseId: '',
            premiseName: '',
            createdby: '',
            declarationForm: '',
            questionnaireId: '',
          });
          setMasterGeofence(undefined);
          clearAllMeasurements();
          onDeleteDrawing(geofenceIndex);

          // Invalidate allGeofences query
          queryClient.invalidateQueries('allGeofences');

          fetchData();
        })
        .catch((err) => console.log(err));
    }
  }

  async function saveGeofence(index, event) {
    event.preventDefault();
    if (event?.target['name']?.value?.length < 1) {
      alert('Field Name cannot be Empty');
      return;
    }
    setIsProcessing(true);
    try {
      let geoPoints = [];
      // let geofenceIndex = geofenceData.findIndex((value) => value.id === index);
      let geofenceIndex = geofenceData.findIndex(
        (value) => (companyFilter ? value._id : value.id) === index
      );
      activeCoordinates.map((data) => geoPoints.push({ longitude: data.lng, latitude: data.lat }));

      const payload = {
        _id: index,
        name: event.target['name'].value,
        premiseId: isVisitor
          ? premises?._id
          : premise?.value || geofenceData[geofenceIndex]?.premiseId,
        speedLimit: isVisitor ? 0 : event.target['speedLimit'].value,
        polygon: geoPoints,
        premiseName: isVisitor
          ? premises?.propertyName
          : premise?.label?.trim() || geofenceData[geofenceIndex]?.premiseName,
        declarationType: isVisitor
          ? 'NONE'
          : selectedChecklist?.value || geofenceData[geofenceIndex]?.declarationType || 'DEFAULT',
        divertAlert: isDivertAlertChecked,
        color: convertTo8DigitHex(color),
        // geofenceTempData.area is in m² (set by updatePolygonMeasurements) — convert to hectares, 2 decimals
        area: Math.round((geofenceTempData.area / 10000) * 100) / 100,
        // edgeDistances: geofenceTempData.edgeDistances,
      };

      // Add questionnaireId if checklist is CUSTOM or DEFAULT
      if (
        (selectedChecklist?.value === 'CUSTOM' || selectedChecklist?.value === 'DEFAULT') &&
        selectedChecklist?.questionnaireId
      ) {
        payload.questionnaireId = selectedChecklist.questionnaireId;
      }

      const data = await updateApiData(
        'geofences/',
        index,
        payload,
        localStorage.getItem('session')
      );

      alert(alertMessageSuccessfullyUpdatedGeofence);
      setGeofenceDetail({
        index: index,
        id: data.data.id,
        name: data.data.name,
        area: Math.round((calculateArea(geofenceData[index]?.polygon) / 10000) * 100) / 100,
        premiseId: data?.data?.premiseId?._id,
        premiseName: data?.data?.premiseId?.propertyName,
        createdby: data.data.createdBy,
        speedLimit: data.data.speedLimit,
        declarationForm: data.data.declarationType ? 'Yes' : 'No',
        divertAlert: data?.data?.divertAlert,
        declarationType: data?.data?.declarationType,
        questionnaireId: data?.data?.questionnaireId,
      });
      geofenceData[geofenceIndex] = data.data;
      setActiveId('');
      setIsProcessing(false);
      setSelectedChecklist(null);
      setMasterGeofence(undefined);
      clearAllMeasurements();

      // Invalidate allGeofences query
      queryClient.invalidateQueries('allGeofences');

      fetchData();
    } catch (error) {
      setIsProcessing(false);
      alert(error?.response?.data?.message);
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllMeasurements();
    };
  }, []);

  // useEffect to handle restriction updates
  useEffect(() => {
    if (!mapRef.current) return;

    const lat = Number(masterGeofence?.masterCentroid?.latitude);
    const lng = Number(masterGeofence?.masterCentroid?.longitude);

    if (
      !masterGeofence ||
      !isValidPremiseRange(masterGeofence?.premiseRange) ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      mapRef.current.setOptions({
        restriction: null,
      });
      return;
    }

    // Set restriction when masterGeofence is available and premiseRange is valid
    const bounds = getBoundsFromRadius(
      {
        lat,
        lng,
      },
      masterGeofence?.premiseRange
    );

    mapRef.current.setOptions({
      restriction: {
        latLngBounds: bounds,
        strictBounds: true,
      },
    });

    // ✅ If we're editing an existing geofence (activeId is set), keep the viewport
    // focused on that geofence itself instead of zooming out to the whole
    // restricted premise window — only the restriction bounds should be the full area.
    if (activeId !== '') {
      const editingPolygonCoords = polygons[activeId];
      if (editingPolygonCoords && editingPolygonCoords.length > 0) {
        const editingBounds = new window.google.maps.LatLngBounds();
        editingPolygonCoords.forEach((point) => {
          editingBounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
        mapRef.current.fitBounds(editingBounds, {
          top: 50,
          bottom: 50,
          left: 50,
          right: 50,
        });
      } else {
        // Fallback if we somehow don't have the polygon's coordinates yet
        mapRef.current.fitBounds(bounds, {
          top: 50,
          bottom: 50,
          left: 50,
          right: 50,
        });
      }
    } else {
      // Auto-size the viewport so the whole allowed window is visible on the map
      mapRef.current.fitBounds(bounds, {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50,
      });
    }
  }, [masterGeofence]);

  // Also add for visitor geofences
  useEffect(() => {
    if (!mapRef.current || !isVisitor) return;

    const lat = Number(masterGeofence?.masterCentroid?.latitude);
    const lng = Number(masterGeofence?.masterCentroid?.longitude);

    if (
      !masterGeofence?.masterCentroid ||
      !isValidPremiseRange(masterGeofence?.premiseRange) ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      mapRef.current.setOptions({
        restriction: null,
      });
      return;
    }

    const bounds = getBoundsFromRadius(
      {
        lat,
        lng,
      },
      masterGeofence?.premiseRange
    );

    mapRef.current.setOptions({
      restriction: {
        latLngBounds: bounds,
        strictBounds: true,
      },
    });

    // ✅ Same edit-focus behavior for visitor geofences: keep the viewport on the
    // geofence being edited instead of the whole restricted premise window.
    if (activeId !== '') {
      const editingPolygonCoords = polygons[activeId];
      if (editingPolygonCoords && editingPolygonCoords.length > 0) {
        const editingBounds = new window.google.maps.LatLngBounds();
        editingPolygonCoords.forEach((point) => {
          editingBounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
        mapRef.current.fitBounds(editingBounds, {
          top: 50,
          bottom: 50,
          left: 50,
          right: 50,
        });
      } else {
        mapRef.current.fitBounds(bounds, {
          top: 50,
          bottom: 50,
          left: 50,
          right: 50,
        });
      }
    } else {
      // Auto-size the viewport so the whole allowed window is visible on the map
      mapRef.current.fitBounds(bounds, {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50,
      });
    }
  }, [isVisitor, masterGeofence?.masterCentroid, masterGeofence?.premiseRange]);

  // ✅ Custom drawing hook — replaces <DrawingManager>.
  // isActive only when premise is selected AND drawingMode is 'polygon'
  const isDrawingPolygon = drawingMode === 'polygon' && isPremiseSelected;
  const { handleMapClick, handleMouseMove, handleDblClick, livePreviewPath } =
    useCustomPolygonDrawing({
      isActive: isDrawingPolygon,
      onPolygonComplete,
    });

  return isLoaded ? (
    <GoogleMap
      zoom={isView ? 10 : geoFence?.zoomLevel || 20}
      center={center}
      onLoad={onLoadMap}
      mapContainerStyle={containerStyle}
      options={{
        zoomControl: true,
        fullscreenControl: true,
        // ✅ Crosshair cursor + disable dbl-click zoom while drawing
        draggableCursor: isDrawingPolygon ? 'crosshair' : undefined,
        disableDoubleClickZoom: isDrawingPolygon,
      }}
      // ✅ These three props power the custom polygon drawing
      onClick={isDrawingPolygon ? handleMapClick : undefined}
      onMouseMove={isDrawingPolygon ? handleMouseMove : undefined}
      onDblClick={isDrawingPolygon ? handleDblClick : undefined}
    >
      {/* ✅ Live preview polygon while user is placing points (replaces DrawingManager's live rubber-band) */}
      {isDrawingPolygon && livePreviewPath.length >= 2 && (
        <Polygon
          paths={livePreviewPath}
          options={{
            fillColor: color,
            fillOpacity: 0.2,
            strokeColor: color,
            strokeWeight: 2,
            zIndex: 9999,
            clickable: false,
          }}
        />
      )}

      {/* ✅ Drawing hint banner */}
      {isDrawingPolygon && (
        <div
          style={{
            position: 'absolute',
            top: 52,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(5, 4, 4, 0.72)',
            color: '#fff',
            padding: '6px 16px',
            borderRadius: 20,
            fontSize: 14,
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          Click to add points · Double-click to finish
        </div>
      )}

      {/* ✅ DrawingManager removed — was here */}

      {/* Render Geofence Polygons */}
      {polygons?.map((iterator, index) => (
        <React.Fragment key={index}>
          {isView && polygons?.length === 1 ? (
            <Polygon
              onLoad={(event) => onLoadPolygon(event, index)}
              onMouseDown={!isView ? () => onClickPolygon(index) : undefined}
              onMouseUp={() => onEditPolygon(index)}
              onDragEnd={() => onEditPolygon(index)}
              options={{
                ...polygonOptions(index),
                zIndex: polygonZIndexMap[index] || 1,
                clickable: !isDrawingPolygon,
              }}
              paths={iterator}
              draggable={true}
              editable={true}
            />
          ) : (
            <Polygon
              onLoad={(event) => onLoadPolygon(event, index)}
              onMouseDown={(e) => drawingMode === '' && onClickPolygon(index)}
              onMouseUp={() => onEditPolygon(index)}
              onDragEnd={() => onEditPolygon(index)}
              options={
                activeId === index
                  ? {
                      fillColor: color,
                      fillOpacity: 0.35,
                      strokeColor: color,
                      strokeWeight: 2,
                      zIndex: polygonZIndexMap[index] || 1,
                      clickable: !isDrawingPolygon,
                    }
                  : {
                      ...polygonOptions(index),
                      zIndex: polygonZIndexMap[index] || 1,
                      clickable: !isDrawingPolygon,
                    }
              }
              paths={iterator}
              draggable={activeId === index && true}
              editable={activeId === index && true}
            />
          )}
        </React.Fragment>
      ))}

      {/* REFACTORED: Friend Location Features */}
      <FriendLocationContainer
        isViewFriendLocation={isViewFriendLocation}
        mapRef={mapRef}
        companyId={userRole?.companyId?._id}
      />

      <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
        <IntlMessages id="input.placeholder.search" defaultMessage="Search Location...">
          {(placeholder) => (
            <MapSearchInput
              placeholder={placeholder}
              type="text"
              style={{ border: '1px solid #E6B471' }}
            />
          )}
        </IntlMessages>
      </Autocomplete>
      {drawingMode !== 'polygon' ? (
        // !isAdmin &&
        canCreateGeofence &&
        (!isAdminOrIsPartner || companyFilter) && (
          <MapButton
            text={<IntlMessages id="modal.title.add.geofence" />}
            onClick={() => {
              toggleDrawingMode();
              setAddGeofenceModal(true);
              setIsPremiseSelected(false);
              setDrawingMode('');
              setShowAddGeofenceInfo(true);
              setIsAddingNewGeofence(true); // user entered add-geofence flow
              setHasLocalDrawnPolygon(false);
            }}
            icon={<AddPlusIcon />}
          />
        )
      ) : (
        <MapButton
          color="error"
          text={<IntlMessages id="modal.button.cancel" />}
          onClick={() => {
            toggleDrawingMode();
            resetGeofenceCreation();
            if (addGeofenceModal === true) {
              setAddGeofenceModal(false);
              onDeleteDrawing();
              setDrawingMode('');
            }
          }}
          icon={<CloseIconWhite />}
        />
      )}
      {geofenceDetail.name !== '' ? (
        <>
          <MapCard className="hide-scroll-bar">
            <div style={{ display: activeId === '' ? 'block' : 'none' }}>
              {!isAdmin ? (
                <>
                  <MapCardContent>
                    <MapHeaderTitle style={{ wordWrap: 'break-word' }}>
                      {isVisitor && 'Geofence:'} {geofenceDetail.name}
                    </MapHeaderTitle>
                    <MapLabel>
                      <IntlMessages id="text.field.area" />:
                    </MapLabel>
                    <MapLabel>{geofenceDetail.area} ha</MapLabel>
                    {!isVisitor && (
                      <>
                        <MapLabel>
                          <IntlMessages id="table.col.premise" />:
                        </MapLabel>
                        <MapLabel>{geofenceDetail.premiseName}</MapLabel>
                      </>
                    )}

                    {!isVisitor && (
                      <>
                        <MapLabel>
                          <IntlMessages id="text.declaration" />:
                        </MapLabel>
                        <MapLabel>{geofenceDetail.declarationForm}</MapLabel>
                      </>
                    )}
                  </MapCardContent>
                  {canManageGeofence && (
                    <MapCardActions>
                      <IconButton
                        onClick={() => deleteGeofence(geofenceDetail.id || geofenceData?._id)}
                      >
                        <DeleteIcon style={{ width: '20px', height: '20px' }} />
                      </IconButton>
                      <IconButton onClick={() => editGeofence(geofenceDetail.id)}>
                        <EditIcon style={{ width: '20px', height: '20px' }} />
                      </IconButton>
                    </MapCardActions>
                  )}
                </>
              ) : companyFilter && isAdminOrIsPartner ? (
                <>
                  <MapCardContent>
                    <MapHeaderTitle style={{ wordWrap: 'break-word' }}>
                      {isVisitor && 'Geofence:'} {geofenceDetail.name}
                    </MapHeaderTitle>
                    <MapLabel>
                      <IntlMessages id="text.field.area" />:
                    </MapLabel>
                    <MapLabel>{geofenceDetail.area} ha</MapLabel>
                    {!isVisitor && (
                      <>
                        <MapLabel>
                          <IntlMessages id="table.col.premise" />:
                        </MapLabel>
                        <MapLabel>{geofenceDetail.premiseName}</MapLabel>
                      </>
                    )}

                    {!isVisitor && (
                      <>
                        <MapLabel>
                          <IntlMessages id="text.declaration" />:
                        </MapLabel>
                        <MapLabel>{geofenceDetail.declarationForm}</MapLabel>
                      </>
                    )}
                  </MapCardContent>
                  {canManageGeofence && (
                    <MapCardActions>
                      <IconButton onClick={() => deleteGeofence(geofenceDetail.id)}>
                        <DeleteIcon style={{ width: '20px', height: '20px' }} />
                      </IconButton>
                      <IconButton onClick={() => editGeofence(geofenceDetail.id)}>
                        <EditIcon style={{ width: '20px', height: '20px' }} />
                      </IconButton>
                    </MapCardActions>
                  )}
                </>
              ) : (
                <>
                  <MapHeaderTitle>
                    <IntlMessages id="modal.title.geofence.details" />
                  </MapHeaderTitle>
                  <br />
                  <br />
                  <MapCardContent>
                    <TwoColumns>
                      <ModalLabel>
                        <IntlMessages id="table.col.account.owner" /> :
                      </ModalLabel>{' '}
                      <ModalLabel isCapitalize={true}>{geofenceDetail?.accountOwner}</ModalLabel>
                    </TwoColumns>
                    <TwoColumns>
                      <ModalLabel>
                        <IntlMessages id="table.col.premise.name" /> :
                      </ModalLabel>{' '}
                      <ModalLabel>{geofenceDetail?.premiseName}</ModalLabel>
                    </TwoColumns>
                    <TwoColumns>
                      <ModalLabel>
                        <IntlMessages id="table.col.geofence.name" /> :
                      </ModalLabel>{' '}
                      <ModalLabel>{geofenceDetail?.name}</ModalLabel>
                    </TwoColumns>
                    <ModalLabel>
                      <IntlMessages id="table.col.creator" /> :
                    </ModalLabel>{' '}
                    <ModalLabel>{geofenceDetail?.createdBy ?? 'N/A'}</ModalLabel>
                  </MapCardContent>
                </>
              )}
            </div>
            <form
              onSubmit={(event) => saveGeofence(geofenceDetail.id, event)}
              style={{ display: activeId !== '' ? 'block' : 'none' }}
            >
              <MapCardContent isGap={true}>
                <MapHeaderTitle style={{ wordWrap: 'break-word' }}>
                  <IntlMessages id="modal.edit.geofence.title" />
                </MapHeaderTitle>
                <InputField
                  Name={<IntlMessages id="input.label.name" />}
                  name="name"
                  variant="standard"
                  size="small"
                  value={geofenceTempData.name}
                  onChange={(event) =>
                    setGeofenceTempData((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
                {!isVisitor && (
                  <DropDown
                    options={formatDropdownData(premises)}
                    selectedOption={handleSelectedPremise}
                    name="premiseId"
                    label={<IntlMessages id="table.col.premise" />}
                    placeholder="Select the premise"
                    defaultValue={geofenceDetail?.premiseName}
                    isDropDownNotClickable={true}
                  />
                )}

                {!isVisitor && (
                  <DropDown
                    options={checklistOptions}
                    selectedOption={handleSelectedChecklist}
                    name="declaration"
                    label="Declaration"
                    placeholder="Select a declaration"
                    defaultValue={
                      (geofenceDetail?.questionnaireId &&
                        checklistOptions.find(
                          (option) => option.questionnaireId === geofenceDetail.questionnaireId
                        )?.label) ||
                      geofenceDetail?.declarationType ||
                      ''
                    }
                  />
                )}
                {!isVisitor && (
                  <>
                    <IntlMessages id="input.label.speed" defaultMessage="Speed">
                      {(Label) => (
                        <InputField
                          Name={Label}
                          name="speedLimit"
                          variant="standard"
                          size="small"
                          value={geofenceTempData.speedLimit}
                          onChange={(event) =>
                            setGeofenceTempData((prev) => ({
                              ...prev,
                              speedLimit: event.target.value,
                            }))
                          }
                        />
                      )}
                    </IntlMessages>
                    <SwitchButtonWrapper>
                      <SwitcherLabel>Divert Alert</SwitcherLabel>
                      <Switch
                        handleToggle={() => setIsDivertAlertChecked(!isDivertAlertChecked)}
                        checked={isDivertAlertChecked}
                      />
                    </SwitchButtonWrapper>
                  </>
                )}
                <IntlMessages id="input.label.color" defaultMessage="Color">
                  {(Label) => (
                    <InputField
                      Name={Label}
                      name="color"
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      style={{
                        width: '100px',
                        height: '60px',
                        border: 'none',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                    />
                  )}
                </IntlMessages>
              </MapCardContent>
              <MapCardActions>
                <OutlineButton
                  onClick={() => {
                    polygons[activePolygonIndex.current] = tempCoordinates;
                    setActiveId('');
                    setMasterGeofence(undefined);
                    clearAllMeasurements();
                  }}
                >
                  <IntlMessages id="modal.button.cancel" />
                </OutlineButton>{' '}
                {isProcessing ? (
                  <Button varient="normal" disabled={isProcessing}>
                    <div style={{ paddingRight: '10px' }}></div>
                    <IntlMessages id="button.text.processing" />
                    <Loader size="small" isNotRequiredLoadingText={true} />
                  </Button>
                ) : (
                  <TextButton type="submit" color="success">
                    <IntlMessages id="modal.button.save" />
                  </TextButton>
                )}
              </MapCardActions>
            </form>
          </MapCard>
        </>
      ) : (
        addGeofenceModal && (
          <MapCard className="hide-scroll-bar">
            <form onSubmit={(event) => addGeofence(geofenceDetail.id, event)}>
              <MapCardContent isGap={true}>
                <MapHeaderContainer>
                  <MapHeaderTitle>
                    <IntlMessages id="modal.title.add.geofence" />
                  </MapHeaderTitle>
                </MapHeaderContainer>{' '}
                {!isVisitor && showAddGeofenceInfo && (
                  <AddGeofenceInfoBanner>
                    <InfoText>Select a property first to draw a geofence.</InfoText>

                    <CloseIcon onClick={() => setShowAddGeofenceInfo(false)}>✕</CloseIcon>
                  </AddGeofenceInfoBanner>
                )}
                {!isVisitor && (
                  <DropDown
                    options={formatDropdownData(premises)}
                    selectedOption={handleSelectedPremiseCreate}
                    name="premiseId"
                    label={<IntlMessages id="table.col.premise" />}
                    placeholder="Select the premise"
                    defaultValue={premise ? premise?.label : geofenceDetail?.premiseName}
                    isDropDownNotClickable={isPremiseLocked}
                    style={{
                      opacity: isPremiseLocked ? 0.6 : 1,
                      pointerEvents: isPremiseLocked ? 'none' : 'auto',
                    }}
                  />
                )}
                <IntlMessages id="table.col.geofence.name" defaultMessage="Geofence Name">
                  {(Label) => (
                    <InputField Name={Label} name="name" variant="standard" size="small" />
                  )}
                </IntlMessages>
                {!isVisitor && (
                  <>
                    <DropDown
                      options={checklistOptions}
                      selectedOption={handleSelectedChecklist}
                      name="declaration"
                      {...(selectedChecklist && { label: 'Select a declaration' })}
                      placeholder="Select a declaration"
                      defaultValue={selectedChecklist?.label}
                    />
                    <IntlMessages id="input.label.speed" defaultMessage="Speed">
                      {(Label) => (
                        <InputField
                          Name={Label}
                          name="speedLimit"
                          variant="standard"
                          size="small"
                        />
                      )}
                    </IntlMessages>
                    <SwitchButtonWrapper>
                      <SwitcherLabel>Divert Alert</SwitcherLabel>
                      <Switch
                        handleToggle={() => setDivertAlert(!divertAlert)}
                        checked={divertAlert}
                      />
                    </SwitchButtonWrapper>
                  </>
                )}
                <IntlMessages id="input.label.color" defaultMessage="Color">
                  {(Label) => (
                    <InputField
                      Name={Label}
                      name="color"
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      style={{
                        width: '100px',
                        height: '60px',
                        border: 'none',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                    />
                  )}
                </IntlMessages>
                <MapCardActionsWrapper>
                  <OutlineButton onClick={resetGeofenceCreation}>
                    <IntlMessages id="modal.button.cancel" />
                  </OutlineButton>
                  {isProcessing ? (
                    <Button varient="normal" disabled={isProcessing}>
                      <div style={{ paddingRight: '10px' }}></div>
                      <IntlMessages id="button.text.processing" />
                      <Loader size="small" isNotRequiredLoadingText={true} />
                    </Button>
                  ) : (
                    <TextButton type="submit" color="success">
                      <IntlMessages id="modal.button.save" />
                    </TextButton>
                  )}
                </MapCardActionsWrapper>
              </MapCardContent>
            </form>
          </MapCard>
        )
      )}
    </GoogleMap>
  ) : null;
};

export default MapComponent;
